<script lang="ts">
  import { onMount } from 'svelte';
  import { Modal } from 'flowbite-svelte';
  import { preloadData } from '$app/navigation';
  import { page } from '$app/stores';
  import { browser } from '$app/environment';
  import { updateUrlParams } from '$lib/updateUrlParams';
  import * as Sentry from '@sentry/sveltekit';
  import type { ComponentType } from 'svelte';

  /** The Svelte component to render inside the modal. */
  export let templateComponent: ComponentType | null = null;

  /** Indicates whether the modal is open or not. */
  let modelOpen = false;

  /** The currently selected location's data. */
  let selectedLocation: Record<string, unknown> | null = null;

  // ✅ React to changes in the URL
  $: {
    const uuid = $page.url.searchParams.get('location-uuid');
    if (uuid) {
      loadLocation(uuid);
    }
  }

  // ✅ Clean up the query param when modal closes
  $: {
    if (!modelOpen && browser && $page.url.searchParams.has('location-uuid')) {
      updateUrlParams({ 'location-uuid': undefined });
    }
  }

  // ✅ Toggle body class based on modal state
  $: {
    if (browser) {
      document.body.classList.toggle('modal-open', modelOpen);
    }
  }

  /**
   * Loads location data based on the provided UUID.
   * Opens the modal if the data loads successfully.
   */
  async function loadLocation(uuid: string | null): Promise<void> {
    if (!browser || !uuid) return;

    const url = `${$page.url.origin}/find-partners/${uuid}`;

    try {
      const result = await preloadData(url);

      if (result.type === 'loaded') {
        if (result.status === 200 && result.data?.uuid) {
          selectedLocation = result.data;
          modelOpen = true;
        } else {
          throw new Error('Invalid location data');
        }
      } else if (result.type === 'redirect') {
        throw new Error(`Unexpected redirect to ${result.location}`);
      } else {
        throw new Error('Unknown preloadData result');
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  /**
   * Closes the modal. Reactive logic will handle URL cleanup.
   */
  function closeModal(): void {
    modelOpen = false;
  }

  onMount(() => {
    if (!browser) return;

    /**
     * Handles external `open-modal` event.
     */
    const handleOpenModal = (event: Event) => {
      const customEvent = event as CustomEvent<{ uuid: string }>;
      const { uuid } = customEvent.detail;
      loadLocation(uuid);
    };

    window.addEventListener('open-modal', handleOpenModal);

    return () => {
      window.removeEventListener('open-modal', handleOpenModal);
    };
  });
</script>

{#if modelOpen}
  <Modal bind:open={modelOpen} outsideclose class="location" classDialog="locationDialog" defaultClass="locationDefault" bodyClass="inner-location" on:close={closeModal}>
    {#if templateComponent}
      <svelte:component this={templateComponent} data={selectedLocation} />
    {/if}
  </Modal>
{/if}
